/**
 * Minimal API client for hub-api. Every call goes through the same operation registry the MCP
 * server uses, so the admin can do exactly what the API allows, and every call is audited.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
    /** On confirm_required: the token to send back with confirm=true (bound to this exact input and preview). */
    public confirmToken?: string,
  ) {
    super(message);
  }
}

/** Sent on every non-GET request made with the session cookie (CSRF protection). */
export const CSRF_HEADER = "X-CRM-Client";

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const method = (init.method ?? (init.json !== undefined ? "POST" : "GET")).toUpperCase();
  if (method !== "GET" && method !== "HEAD") headers.set(CSRF_HEADER, "admin");
  let body = init.body;
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }
  const res = await fetch(path, { ...init, method, headers, body, credentials: "same-origin" });
  const text = await res.text();
  const data = text ? safeJSON(text) : undefined;
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown; confirm_token?: string } } | undefined)?.error;
    throw new ApiError(res.status, err?.code ?? "http_" + res.status, err?.message ?? res.statusText, err?.details, err?.confirm_token);
  }
  return data as T;
}

function safeJSON(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

/** Query-string helper that drops empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? "?" + s : "";
}

/** POST a JSON body and save the response as a file (CSV exports). Returns the file name used. */
export async function downloadFile(path: string, body: unknown, fallbackName: string): Promise<string> {
  const res = await fetch(path, {
    method: body === undefined ? "GET" : "POST",
    headers: body === undefined ? {} : { "Content-Type": "application/json", [CSRF_HEADER]: "admin" },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: "same-origin",
  });
  if (!res.ok) {
    const data = safeJSON(await res.text()) as { error?: { code?: string; message?: string } };
    throw new ApiError(res.status, data?.error?.code ?? "http_" + res.status, data?.error?.message ?? res.statusText);
  }
  const name = /filename="?([^";]+)"?/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return name;
}

/** Multipart upload (one file in field "file"). */
export async function uploadFile<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  return api<T>(path, { method: "POST", body: form });
}
